import styled from "styled-components";

import PageSizeSelect from "@/pages/conciliation/deposit-reconciliation/components/page-size-select";
import { getPagesToShow } from "@/utils/pagination";
import type { Meta } from "src/@types/paginated";

import { DEPOSIT_MAINTENANCE_LABELS, FIRST_PAGE } from "../types";

interface DepositPaginationProps {
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly isFetching: boolean;
  readonly onPageChange: (page: number) => void;
  readonly onPageSizeChange: (size: number) => void;
}

export function DepositPagination({
  page,
  pageSize,
  total,
  isFetching,
  onPageChange,
  onPageSizeChange,
}: DepositPaginationProps) {
  const lastPage: number = Math.max(FIRST_PAGE, Math.ceil(total / pageSize));
  const meta: Meta = {
    currentPage: page,
    lastPage,
    perPage: pageSize,
    total,
    prev: page > FIRST_PAGE ? page - 1 : null,
    next: page < lastPage ? page + 1 : null,
  };

  return (
    <div className="flex w-full items-center justify-between px-3 py-2 text-xs border-t border-slate-200">
      <PaginationButtonsContainer>
        {total > 0 && (
          <>
            <button
              className={`navigate-btn previous ${
                meta.prev !== null && !isFetching ? "" : "disabled"
              }`}
              onClick={() => meta.prev !== null && onPageChange(meta.prev)}
              disabled={meta.prev === null || isFetching}
            >
              {DEPOSIT_MAINTENANCE_LABELS.previousPage}
            </button>
            {getPagesToShow(meta).map((shown: string | number, index: number) => (
              <span
                key={`${shown}-${index}`}
                className={`pages ${shown === meta.currentPage ? "selected" : ""} ${
                  shown === "..." ? "ocult" : ""
                }`}
                onClick={() => typeof shown === "number" && onPageChange(shown)}
              >
                {shown}
              </span>
            ))}
            <button
              className={`navigate-btn next ${
                meta.next !== null && !isFetching ? "" : "disabled"
              }`}
              onClick={() => meta.next !== null && onPageChange(meta.next)}
              disabled={meta.next === null || isFetching}
            >
              {DEPOSIT_MAINTENANCE_LABELS.nextPage}
            </button>
          </>
        )}
      </PaginationButtonsContainer>

      <div className="flex items-center gap-4">
        <PageSizeSelect value={pageSize} onChange={onPageSizeChange} />
        <span className="text-slate-600">
          {total}{" "}
          {total === 1
            ? DEPOSIT_MAINTENANCE_LABELS.depositSingular
            : DEPOSIT_MAINTENANCE_LABELS.depositPlural}
        </span>
      </div>
    </div>
  );
}

const PaginationButtonsContainer = styled.div`
  display: flex;
  font-size: 14px;

  .pages {
    display: flex;
    justify-content: center;
    padding: 8px 6px;
    border: 1px solid #dcdce3;
    border-left: none;
    border-top: none;
    min-width: 29px;

    &.selected {
      background-color: #f4c83e;
    }
  }

  .pages:not(.ocult) {
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .navigate-btn {
    cursor: pointer;
    padding: 6px 12px;
    background-color: #2b0c1a;
    color: #fff;

    &.previous {
      border-radius: 0 0 0 8px;
    }

    &.next {
      border-radius: 0 0 8px 0;
    }

    &:hover {
      text-decoration: underline;
    }

    &.disabled {
      color: #8d91a2;
      background-color: #d9dae0;
      cursor: not-allowed;
      text-decoration: none;
    }
  }
`;
